// Story 9.4 (ADO-1948): the Listbox, a labelled dropdown that picks one value from a
// short list. Figma "Sort by" 5206:7404 (the field) and "Sort menu" 5206:7666 (the popup).
//
// The WAI-ARIA APG "select-only combobox" pattern: the trigger is `role="combobox"`, the
// popup is `role="listbox"` of `role="option"` rows, and DOM focus NEVER leaves the
// trigger. The highlighted row is conveyed by `aria-activedescendant`, so a screen reader
// announces each row as the arrows move while the trigger keeps focus for the next key.
//
// ── Why not the design-system `Select` ──
// `Select` is a styled native <select>, whose popup is drawn by the browser and cannot
// carry the frame's selected-row tint and check mark. This control draws its own popup,
// and so owns the keyboard model the native element would otherwise have supplied:
//
//   closed  Enter, Space, ArrowDown, ArrowUp open on the selected option;
//           Home and End open on the first and last option.
//   open    ArrowDown / ArrowUp move one row (no wrap), Home / End jump to the ends,
//           Enter / Space select the highlighted row and close, Escape closes with no
//           change, Tab closes with no change and lets focus move on.
//
// Mouse: a click on the trigger toggles the popup, a click on a row selects it and
// closes, and a pointer press anywhere outside the control closes it with no change.
//
// `onChange` fires only when the chosen value differs from `value`: re-choosing the
// current value is not a change, and a caller that records each change (browser history,
// say) should not gain an entry for it.

import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Icon } from '../Icon/Icon';
import { Modal } from '../Modal/Modal';
import styles from './Listbox.module.css';

export interface ListboxOption<Value extends string> {
  readonly value: Value;
  readonly label: string;
}

interface ListboxBaseProps<Value extends string> {
  /** The visible label above the trigger, e.g. "Sort by". */
  readonly label: string;
  readonly options: readonly ListboxOption<Value>[];
  /** The chosen value. Controlled: the trigger shows this option's label. */
  readonly value: Value;
  /** Called with a newly chosen value; never with the value already chosen. */
  readonly onChange: (value: Value) => void;
  /** The trigger's id. Generated when omitted. */
  readonly id?: string;
  /** Extra class on the outer field, for the caller's own width or placement. Accepts
   *  `undefined` because a CSS-module class lookup is typed that way. */
  readonly className?: string | undefined;
  /**
   * ADO-1779: where the options open. `popup` is the anchored menu under the trigger
   * (the default). `sheet` is the mobile bottom sheet (Figma "Sort open" 5415:23154): the
   * same trigger opens a dialog titled with the label, listing the options as full-width
   * rows, and choosing one closes it.
   */
  readonly presentation?: 'popup' | 'sheet';
}

/** The `popup` presentation (the default). */
export interface PopupListboxProps<Value extends string> extends ListboxBaseProps<Value> {
  readonly presentation?: 'popup';
  /** Story 8.13 (ADO-2022): an error message; when set the field is invalid (`aria-invalid`)
   *  and the message is announced and described. */
  readonly error?: ReactNode;
  /** Story 8.13 (ADO-2022): the field must have a value (`aria-required`). */
  readonly required?: boolean;
  /** Story 8.13 (ADO-2022): read-only for now (`aria-disabled`, out of the tab
   *  order, ignoring clicks and keys). The trigger is a `div`, which a disabled `<fieldset>`
   *  does not reach, so a form that disables its controls passes this too. */
  readonly disabled?: boolean;
}

/** The `sheet` presentation. Its trigger is a button, which ARIA gives neither an invalid nor a
 *  required state, so the types refuse the popup's `error`, `required` and `disabled` here
 *  (Story 8.13, ADO-2022). */
export interface SheetListboxProps<Value extends string> extends ListboxBaseProps<Value> {
  readonly presentation: 'sheet';
  readonly error?: never;
  readonly required?: never;
  readonly disabled?: never;
}

export type ListboxProps<Value extends string> =
  | PopupListboxProps<Value>
  | SheetListboxProps<Value>;

export function Listbox<Value extends string>(props: ListboxProps<Value>) {
  return props.presentation === 'sheet' ? <SheetListbox {...props} /> : <PopupListbox {...props} />;
}

function PopupListbox<Value extends string>({
  label,
  options,
  value,
  onChange,
  id,
  className,
  error,
  required,
  disabled = false,
}: PopupListboxProps<Value>) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const labelId = `${baseId}-label`;
  const valueId = `${baseId}-value`;
  const listboxId = `${baseId}-listbox`;
  const errorId = error ? `${baseId}-error` : undefined;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const [open, setOpen] = useState(false);
  /** The highlighted row while open. Meaningless while closed. */
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const selectedIndex = options.findIndex((option) => option.value === value);
  // Story 8.13 (ADO-2022): a value no longer among the options (a collection slot that has
  // passed) is still shown, so a refusal of it points at what the Shopper chose.
  const selectedLabel = options[selectedIndex]?.label ?? value;
  const lastIndex = options.length - 1;

  const openAt = (index: number) => {
    // Nothing to choose from, so nothing to open: an open, empty listbox would point
    // `aria-activedescendant` at a row that does not exist.
    if (lastIndex < 0) return;
    setActiveIndex(Math.min(Math.max(index, 0), lastIndex));
    setOpen(true);
  };

  const choose = (index: number) => {
    const option = options[index];
    setOpen(false);
    if (option !== undefined && option.value !== value) onChange(option.value);
  };

  // A press outside the control closes it. The trigger and the popup are both inside
  // the wrapper, so pressing either is excluded: the trigger's own click owns the
  // toggle, and a row's click owns the selection.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target !== null && wrapperRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Keep the highlighted row visible when the list is taller than its popup (it scrolls
  // past eight rows). Read off this instance's own popup rather than by id, so two
  // controls given the same `id` cannot scroll each other's rows.
  useEffect(() => {
    if (!open) return;
    popupRef.current?.children[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!open) {
      switch (event.key) {
        case 'Enter':
        case ' ':
        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault();
          openAt(selectedIndex);
          return;
        case 'Home':
          event.preventDefault();
          openAt(0);
          return;
        case 'End':
          event.preventDefault();
          openAt(lastIndex);
          return;
        default:
          return;
      }
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, lastIndex));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        return;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        return;
      case 'End':
        event.preventDefault();
        setActiveIndex(lastIndex);
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(activeIndex);
        return;
      case 'Escape':
        // Stopped here so a surrounding dialog does not also treat it as its own dismiss.
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        return;
      case 'Tab':
        // Not prevented: focus moves on as it normally would, and nothing is chosen.
        setOpen(false);
        return;
      default:
        return;
    }
  };

  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')} ref={wrapperRef}>
      {/* Not a <label>: a `role="combobox"` div is not a labelable element, so the
          trigger and the popup reference this by id instead. */}
      <span id={labelId} className={styles.label}>
        {label}
      </span>

      <div className={styles.control}>
        <div
          id={baseId}
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled ? true : undefined}
          className={[styles.trigger, error ? styles.invalid : ''].filter(Boolean).join(' ')}
          aria-invalid={error ? true : undefined}
          aria-required={required === true ? true : undefined}
          aria-describedby={errorId}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-labelledby={`${labelId} ${valueId}`}
          {...(open ? { 'aria-activedescendant': optionId(activeIndex) } : {})}
          onClick={() => {
            if (disabled) return;
            if (open) setOpen(false);
            else openAt(selectedIndex);
          }}
          onKeyDown={disabled ? undefined : onKeyDown}
          onBlur={() => setOpen(false)}
        >
          <span id={valueId} className={styles.value}>
            {selectedLabel}
          </span>
          <Icon name="caretDown" size={18} aria-hidden className={styles.caret} />
        </div>

        {/* Always in the DOM so `aria-controls` always resolves; hidden while closed.
            Neither the popup nor its rows take focus: in the `aria-activedescendant`
            pattern focus stays on the combobox, which owns the whole keyboard model, so
            the focusability and key-listener rules below do not apply to them. */}
        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
        <div
          id={listboxId}
          ref={popupRef}
          role="listbox"
          aria-labelledby={labelId}
          className={styles.popup}
          hidden={!open}
          // A press on a row must not move focus off the trigger (which would blur and
          // close the popup before the row's click could select it).
          onMouseDown={(event) => event.preventDefault()}
        >
          {options.map((option, index) => {
            const selected = option.value === value;
            return (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
              <div
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={selected}
                data-active={open && index === activeIndex}
                className={styles.option}
                onClick={() => choose(index)}
                onPointerMove={() => setActiveIndex(index)}
              >
                <span className={styles.optionLabel}>{option.label}</span>
                {selected ? (
                  <Icon name="check" size={16} aria-hidden className={styles.check} />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
      {error ? (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The `sheet` presentation. The trigger is a plain button that opens a bottom-sheet
 * `Modal` (which brings the dialog contract: focus trap, Escape, scrim press, scroll lock,
 * focus back to the trigger on close). Inside it the options are a `role="listbox"` whose
 * rows take DOM focus themselves (the APG listbox with a roving tabindex, rather than the
 * popup's `aria-activedescendant`, because here focus has left the trigger for the
 * dialog). Arrow keys, Home and End move between rows, Enter, Space or a tap choose one,
 * and the sheet opens with the chosen row focused.
 */
function SheetListbox<Value extends string>({
  label,
  options,
  value,
  onChange,
  id,
  className,
}: SheetListboxProps<Value>) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const labelId = `${baseId}-label`;
  const valueId = `${baseId}-value`;
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedIndex = options.findIndex((option) => option.value === value);
  // Story 8.13 (ADO-2022): a value no longer among the options (a collection slot that has
  // passed) is still shown, so a refusal of it points at what the Shopper chose.
  const selectedLabel = options[selectedIndex]?.label ?? value;
  const [focusIndex, setFocusIndex] = useState(Math.max(selectedIndex, 0));

  // Runs after the Modal's own initial focus (a child's effects run first), so the
  // chosen row, not the close button, is where a keyboard user starts.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
  }, [open]);

  const openSheet = () => {
    setFocusIndex(Math.max(selectedIndex, 0));
    setOpen(true);
  };

  const choose = (index: number) => {
    const option = options[index];
    setOpen(false);
    if (option !== undefined && option.value !== value) onChange(option.value);
  };

  const moveTo = (index: number) => {
    const next = Math.min(Math.max(index, 0), options.length - 1);
    setFocusIndex(next);
    listRef.current?.querySelectorAll<HTMLElement>('[role="option"]')[next]?.focus();
  };

  const onOptionKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    const moves: Record<string, number> = {
      ArrowDown: index + 1,
      ArrowUp: index - 1,
      Home: 0,
      End: options.length - 1,
    };
    const target = moves[event.key];
    if (target !== undefined) {
      event.preventDefault();
      moveTo(target);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(index);
    }
  };

  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')}>
      <span id={labelId} className={styles.label}>
        {label}
      </span>
      <button
        type="button"
        id={baseId}
        className={styles.trigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-labelledby={`${labelId} ${valueId}`}
        onClick={openSheet}
      >
        <span id={valueId} className={styles.value}>
          {selectedLabel}
        </span>
        <Icon name="caretDown" size={18} aria-hidden className={styles.caret} />
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={label}
        placement="bottom"
        sheetGutter="compact"
      >
        <div ref={listRef} role="listbox" aria-label={label} className={styles.sheetList}>
          {options.map((option, index) => {
            const selected = option.value === value;
            return (
              <div
                key={option.value}
                role="option"
                aria-selected={selected}
                tabIndex={index === focusIndex ? 0 : -1}
                className={`${styles.option} ${styles.sheetOption}`}
                onClick={() => choose(index)}
                onKeyDown={(event) => onOptionKeyDown(event, index)}
              >
                <span className={styles.optionLabel}>{option.label}</span>
                {selected ? (
                  <Icon name="check" size={16} aria-hidden className={styles.check} />
                ) : null}
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
